import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Alert,
} from '@mui/material';
import { AppLayout } from '../components/layout/AppLayout';
import { BackButton } from '../components/common/BackButton';
import { useAuth } from '../hooks/useAuth';
import { useClinic } from '../hooks/useClinic';
import { useNavigate } from 'react-router-dom';
import { referralService, ReferralData } from '../services/referralService';
import { platformDetector } from '../services/platformDetector';


export const RecommendClinicPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clinic } = useClinic();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [referralData, setReferralData] = useState<ReferralData | null>(null);

  // ЗАГРУЗКА РЕФЕРАЛЬНОГО КОДА И QR
  useEffect(() => {
    const loadReferralData = async () => {
      try {
        setLoading(true);
        const result = await referralService.getReferral();
        setReferralData(result);
      } catch (e) {
        console.error('Ошибка при загрузке реферального кода:', e);
        setError('Не удалось загрузить реферальный код');
      } finally {
        setLoading(false);
      }
    };

    loadReferralData();
  }, []);

  // ПОДЕЛИТЬСЯ ССЫЛКОЙ
  const handleShare = async () => {
    if (!referralData?.referralUrl) return;

    const shareText = `Хочу порекомендовать эту клинику, перейди по ссылке: ${referralData.referralUrl} и получи бонусные баллы!`;

    try {
      // Всегда копируем в буфер обмена
      await navigator.clipboard.writeText(shareText);

      // Затем пробуем открыть share в зависимости от платформы
      if (platformDetector.isTelegram()) {
        const telegramWebApp = (window as any).Telegram?.WebApp;

        if (telegramWebApp?.switchInlineQuery) {
          try {
            telegramWebApp.switchInlineQuery(shareText, true);
            return;
          } catch (err) {
            console.warn('switchInlineQuery ошибка:', err);
          }
        }

        if (telegramWebApp?.shareToChat) {
          try {
            telegramWebApp.shareToChat(referralData.referralUrl);
            return;
          } catch (err) {
            console.warn('shareToChat ошибка:', err);
          }
        }
      } else if (platformDetector.isMax()) {
        const maxWebApp = (window as any).WebApp;

        if (maxWebApp?.switchInlineQuery) {
          try {
            maxWebApp.switchInlineQuery(shareText, true);
            return;
          } catch (err) {
            console.warn('MAX switchInlineQuery ошибка:', err);
          }
        }

        if (maxWebApp?.openLink) {
          try {
            maxWebApp.openLink(referralData.referralUrl);
            return;
          } catch (err) {
            console.warn('MAX openLink ошибка:', err);
          }
        }
      }

      // Если мессенджер API не работает, показываем что скопировано
      alert('✅ Ссылка скопирована в буфер обмена!\n\nОткройте чат и вставьте чтобы поделиться');
    } catch (error) {
      console.error('Ошибка:', error);
      alert('❌ Не удалось скопировать ссылку');
    }
  };

  if (loading) {
    return (
      <AppLayout title="Посоветовать">
        <Box sx={{ pb: 8, px: 1, display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Посоветовать">
        <Box sx={{ pb: 8, px: 1 }}>
          <BackButton onClick={() => navigate(-1)} />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Посоветовать">
      <Box sx={{ pb: 8, px: 1 }}>
        <BackButton onClick={() => navigate(-1)} />

        <Typography
          sx={{
            fontSize: { xs: 28, sm: 32, md: 36 },
            fontWeight: 600,
            color: '#212121',
            mb: 1,
            lineHeight: 1,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          Рекомендовать клинику
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 500,
            color: 'rgba(33,33,33,0.7)',
            mb: 2.5,
            lineHeight: 1.2,
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          Нажмите кнопку «порекомендовать» <br /> или покажите QR-код
        </Typography>

        {/* QR Код */}
        {referralData?.qrCode && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            <Paper
              sx={{
                width: '100%',
                maxWidth: { xs: '280px', sm: '359px' },
                aspectRatio: '1 / 1',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#FFFFFF',
              }}
            >
              <img
                src={referralData.qrCode}
                alt="QR Code"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: '16px',
                }}
              />
            </Paper>
          </Box>
        )}


        {/* Кнопка поделиться */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleShare}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: '359px' },
              height: { xs: 48, sm: 54, md: 58 },
              backgroundColor: '#007AFF',
              borderRadius: '22px',
              fontWeight: 600,
              fontSize: { xs: 14, sm: 16 },
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(0, 122, 255, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                backgroundColor: '#0055CC',
                boxShadow: '0 8px 24px rgba(0, 122, 255, 0.4)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: '0 2px 8px rgba(0, 122, 255, 0.2)',
              },
              px: { xs: 1, sm: 0 },
            }}
          >
            Порекомендовать
          </Button>
        </Box>

      </Box>
    </AppLayout>
  );
};
